import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { wayleaveDocsAPI } from '../utils/api';

const outcomeColorFor = (outcome) =>
  outcome === 'approved' ? '#059669' : outcome === 'declined' ? '#ef4444' : '#f59e0b';

// Slot status colours (mirrors WayleaveDocuments / spec §4)
const SLOT_STATUS = {
  not_started: { label: 'Not Started', color: '#9ca3af' },
  submitted: { label: 'Awaiting Review', color: '#f59e0b' },
  in_review: { label: 'Under Review', color: '#3b82f6' },
  changes_requested: { label: 'Changes Requested', color: '#f97316' },
  accepted: { label: 'Accepted', color: '#10b981' },
  submitted_to_dre: { label: 'With DRE', color: '#6366f1' },
  dre_query: { label: 'DRE Query', color: '#ef4444' },
  approved: { label: 'Approved', color: '#059669' },
  not_applicable: { label: 'N/A', color: '#9ca3af' },
};
const slotMeta = (s) => SLOT_STATUS[s] || SLOT_STATUS.not_started;

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

function StatusChip({ status }) {
  const meta = slotMeta(status);
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}
    >
      <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: meta.color }} />
      {meta.label}
    </span>
  );
}

function DecisionForm({ app, onCancel, onDone }) {
  const [mode, setMode] = useState(null); // 'approved' | 'declined' | 'query'
  const [reason, setReason] = useState('');
  const [affected, setAffected] = useState([]);
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleAffected = (key) =>
    setAffected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = { outcome: mode };
      if (mode === 'declined') payload.reason = reason;
      if (mode === 'query') { payload.reason = reason; payload.affected = affected; }
      if (mode === 'approved') { payload.validFrom = validFrom || null; payload.validTo = validTo || null; }
      await wayleaveDocsAPI.decideSubmission(app.submission.id, payload);
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record decision');
    } finally {
      setSaving(false);
    }
  };

  if (!mode) {
    return (
      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={() => setMode('approved')} className="px-3 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">Approve</button>
        <button onClick={() => setMode('query')} className="px-3 py-2 text-sm font-medium bg-amber-500 text-white rounded-lg hover:bg-amber-600">Query</button>
        <button onClick={() => setMode('declined')} className="px-3 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700">Decline</button>
        <button onClick={onCancel} className="px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
      </div>
    );
  }

  return (
    <div className="mt-3 border-t border-gray-100 pt-3 space-y-3">
      {mode === 'approved' && (
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Valid from <span className="text-gray-400">(optional)</span></label>
            <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Valid to <span className="text-gray-400">(optional)</span></label>
            <input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
          </div>
        </div>
      )}

      {(mode === 'declined' || mode === 'query') && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder={mode === 'query' ? 'What needs to change?' : 'Why is this being declined?'}
          />
        </div>
      )}

      {mode === 'query' && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1.5">Which documents are affected? <span className="text-red-500">*</span></label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {app.slots.map((s) => (
              <label key={s.doc_key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={affected.includes(s.doc_key)} onChange={() => toggleAffected(s.doc_key)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                {s.name}
              </label>
            ))}
          </div>
          <p className="text-[11px] text-gray-400 mt-1">Only the documents you tick are reopened for the contractor; the rest stay locked.</p>
        </div>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}

      <div className="flex gap-2">
        <button
          onClick={submit}
          disabled={saving || ((mode === 'declined' || mode === 'query') && !reason.trim()) || (mode === 'query' && affected.length === 0)}
          className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : `Confirm ${mode === 'approved' ? 'approval' : mode}`}
        </button>
        <button onClick={() => setMode(null)} disabled={saving} className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">Back</button>
      </div>
    </div>
  );
}

function ApplicationCard({ app, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const sub = app.submission;
  const decided = !!sub?.outcome;

  const outcomeColor = sub?.outcome === 'approved' ? '#059669' : sub?.outcome === 'declined' ? '#ef4444' : '#f59e0b';

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900">Station {app.station_no}</h3>
            {app.owner && <span className="text-xs text-gray-500">{app.owner}</span>}
            {decided && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium capitalize" style={{ backgroundColor: `${outcomeColor}1a`, color: outcomeColor }}>
                {sub.outcome}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Submitted {fmtDate(sub?.submitted_at)} by {sub?.submitted_by || '—'}
            {sub?.dre_ref ? ` · Ref ${sub.dre_ref}` : ''}
          </p>
          {decided && (
            <p className="text-xs mt-1" style={{ color: outcomeColor }}>
              {sub.outcome === 'approved'
                ? `Approved${sub.valid_from ? ` · valid ${fmtDate(sub.valid_from)}${sub.valid_to ? ` – ${fmtDate(sub.valid_to)}` : ''}` : ''}`
                : `${sub.outcome === 'query' ? 'Queried' : 'Declined'}${sub.outcome_reason ? ` — ${sub.outcome_reason}` : ''}`}
              {sub.decided_by ? ` (by ${sub.decided_by})` : ''}
            </p>
          )}
        </div>
        <button onClick={() => setExpanded((v) => !v)} className="text-sm text-primary-600 hover:text-primary-800 font-medium">
          {expanded ? 'Hide documents' : 'View documents'}
        </button>
      </div>

      {expanded && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rev</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {app.slots.map((s) => (
                  <tr key={s.id}>
                    <td className="px-3 py-2 text-sm font-medium text-gray-900">{s.name}</td>
                    <td className="px-3 py-2"><StatusChip status={s.status} /></td>
                    <td className="px-3 py-2 text-sm text-gray-600">{s.revision_no != null ? `R${s.revision_no}` : '—'}</td>
                    <td className="px-3 py-2 text-sm">
                      {s.fileUrl
                        ? <a href={s.fileUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-800">View</a>
                        : <span className="text-gray-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!decided && (
        deciding
          ? <DecisionForm app={app} onCancel={() => setDeciding(false)} onDone={() => { setDeciding(false); onChanged(); }} />
          : <button onClick={() => setDeciding(true)} className="mt-3 px-3 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700">Record decision</button>
      )}
    </div>
  );
}

function WayleaveDre() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await wayleaveDocsAPI.getSubmissions();
      setApps(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pending = apps.filter((a) => !a.submission?.outcome);
  const decided = apps.filter((a) => a.submission?.outcome);

  const coordApps = apps.filter((a) => a.lat != null && a.lng != null);
  const mapCenter = coordApps.length
    ? [coordApps.reduce((s, a) => s + a.lat, 0) / coordApps.length, coordApps.reduce((s, a) => s + a.lng, 0) / coordApps.length]
    : [-33.9, 19.0];

  return (
    <div className="w-full">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">DRE Review</h2>
        <p className="text-sm text-gray-600">Wayleave document packs submitted for your decision.</p>
      </div>

      {coordApps.length > 0 && (
        <div className="card mb-4">
          <div className="rounded-lg overflow-hidden" style={{ height: '300px' }}>
            <MapContainer center={mapCenter} zoom={7} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
              {coordApps.map((a) => {
                const color = outcomeColorFor(a.submission?.outcome);
                return (
                  <CircleMarker key={a.id} center={[a.lat, a.lng]} radius={8} pathOptions={{ color, fillColor: color, fillOpacity: 0.75, weight: 1 }}>
                    <Popup>
                      <div className="text-sm">
                        <div className="font-semibold">Station {a.station_no}{a.owner ? ` · ${a.owner}` : ''}</div>
                        <div className="capitalize">{a.submission?.outcome ? `DRE ${a.submission.outcome}` : 'Awaiting decision'}</div>
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-600">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#059669' }} /> Approved</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#ef4444' }} /> Declined</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#f59e0b' }} /> In process</span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="card text-center py-12 text-gray-500">Loading submissions…</div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      ) : apps.length === 0 ? (
        <div className="card text-center py-12 text-gray-500">Nothing submitted yet.</div>
      ) : (
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Awaiting decision ({pending.length})</h3>
            {pending.length === 0 ? (
              <div className="card text-center py-8 text-gray-400 text-sm">Nothing waiting on you.</div>
            ) : (
              <div className="space-y-3">
                {pending.map((a) => <ApplicationCard key={a.id} app={a} onChanged={load} />)}
              </div>
            )}
          </div>

          {decided.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Decided ({decided.length})</h3>
              <div className="space-y-3">
                {decided.map((a) => <ApplicationCard key={a.id} app={a} onChanged={load} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default WayleaveDre;
